import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle } from 'lucide-react';

// Simple pub/sub so any module (or window.alert override) can trigger the dialog
const listeners = new Set();
export const showAppAlert = (message, options = {}) => {
  const payload = { message: String(message ?? ''), ...options };
  listeners.forEach((l) => l(payload));
};

/**
 * Installs an override on window.alert so legacy alert() calls render
 * the themed modal instead of the browser-native dialog with a blue button.
 */
export const installAppAlertOverride = () => {
  if (typeof window === 'undefined') return;
  if (window.__appAlertInstalled) return;
  window.__appAlertInstalled = true;
  window.alert = (msg) => showAppAlert(msg);
};

export default function AppAlert() {
  const [item, setItem] = useState(null);
  const okBtnRef = useRef(null);

  useEffect(() => {
    const handler = (payload) => setItem(payload);
    listeners.add(handler);
    return () => listeners.delete(handler);
  }, []);

  useEffect(() => {
    if (item && okBtnRef.current) {
      okBtnRef.current.focus();
    }
  }, [item]);

  const close = () => setItem(null);

  // Close on Escape
  useEffect(() => {
    if (!item) return;
    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={close}
          data-testid="app-alert-overlay"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 24 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            data-testid="app-alert-dialog"
          >
            <div className="flex items-start gap-4 p-6">
              <div className="flex-shrink-0 w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <AlertCircle size={22} />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="font-heading font-semibold text-slate-900 text-base mb-1">
                  {item.title || 'Pemberitahuan'}
                </h3>
                <p
                  className="text-sm text-slate-600 leading-relaxed whitespace-pre-line"
                  data-testid="app-alert-message"
                >
                  {item.message}
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 pt-2 flex justify-end">
              <button
                ref={okBtnRef}
                onClick={close}
                className="btn-primary min-w-[96px]"
                data-testid="app-alert-ok-btn"
              >
                OK
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
